import { Box, Button, Typography } from '@mui/material';
import BackendService from 'BackendService';
import { CommonLoggedInDataContext, CommonLoggedInDataWithToken } from 'CommonLoggedInData';
import React from 'react';
import ContactDetails from '../../models/ContactDetails';
import { googleAnalyticsService } from '../../services/GoogleAnalyticsService';
import {
  flushClientLogs,
  startClientLogCapture,
  stopClientLogCapture,
} from '../../services/clientLogCapture';
import TimerDisplay from '../TimerDisplay';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';

interface State {
  impersonatingName?: string;
  impersonationExpires?: number;
  impersonationCaptureLogs?: boolean;
}

export default class HeaderCenterContent extends React.Component<any, State> {
  static contextType = CommonLoggedInDataContext;
  context!: React.ContextType<typeof CommonLoggedInDataContext>;

  state: State = {};

  componentDidMount() {
    new BackendService().getUserNameAndInitials().then(
      (
        data: ContactDetails & {
          impersonating: boolean;
          impersonationExpires: number;
          impersonationCaptureLogs: boolean;
          actorSub: string | null;
          targetSub: string | null;
        },
      ) => {
        // Set user properties in Google Analytics using only the sub (no PII)
        const context = this.context as CommonLoggedInDataWithToken;
        if (context?.loggedInData?.email && googleAnalyticsService.getInitialized()) {
          googleAnalyticsService.setUserProperties({
            user_id: data.impersonating ? data.actorSub : context.loggedInData.email, // This is the sub from Auth0
            impersonated_user_id: data.impersonating ? data.targetSub : null,
          });

          // Note: Login tracking should be handled at actual login time, not on every page load
        }

        if (data.impersonating) {
          this.setState({
            impersonatingName: data.name,
            impersonationExpires: data.impersonationExpires,
            impersonationCaptureLogs: data.impersonationCaptureLogs,
          });
          if (data.impersonationCaptureLogs) {
            startClientLogCapture((entries) =>
              new BackendService().sendImpersonationClientLogs(entries),
            );
          }
        }
      },
    );
  }

  handleExit = () => {
    // Note: This is impersonation exit, not actual logout
    void flushClientLogs();
    stopClientLogCapture();
    new BackendService()
      .exitImpersonation()
      .then(() => window.close())
      .catch(() => window.close());
  };

  render() {
    const { impersonatingName, impersonationExpires, impersonationCaptureLogs } = this.state;
    if (!impersonatingName) {
      return <></>;
    }
    return (
      <Box sx={{ textAlign: 'center', flexGrow: 1 }}>
        {impersonatingName && (
          <StyledTypography variant="body2" color="warning.main">
            Viewing as {impersonatingName}{' '}
            {impersonationCaptureLogs && (
              <Typography component="span" variant="caption" data-testid="impersonation-with-logs">
                with logs{' '}
              </Typography>
            )}
            {impersonationExpires && (
              <TimerDisplay
                prefixLabel={{
                  normal: 'Expires in',
                  urgent: 'Window will close in',
                }}
                urgentWithSecondsLeft={120}
                impersonationExpires={impersonationExpires}
                onExpired={this.handleExit}
              />
            )}
            <Button
              size="small"
              color="inherit"
              onClick={this.handleExit}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                border: `1px solid ${Palette.warning.background}`,
                backgroundColor: Palette.warning.buttonBg,
                color: Palette.warning.buttonText,
              }}
            >
              Exit
            </Button>
          </StyledTypography>
        )}
      </Box>
    );
  }
}
